import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AgentUsageSparkline — mini-sparkline SVG (30 jours) pour la page Agents.
 *
 * Aucune dépendance externe : SVG natif, ~50 lignes.
 * Récupère la série quotidienne via /workspace/agents/{slug}/usage?days=30.
 */
export default function AgentUsageSparkline({ slug, color = "#378ADD", days = 30 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/workspace/agents/${slug}/usage?days=${days}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, days]);

  if (error || !data) {
    return (
      <div
        className="text-[10px] text-zinc-600 italic"
        data-testid={`agent-sparkline-empty-${slug}`}
      >
        —
      </div>
    );
  }

  const series = data.series || [];
  const max = Math.max(1, data.max_day || 0);
  const w = 120;
  const h = 24;
  const barW = w / Math.max(1, series.length);
  const total = data.total_calls || 0;

  return (
    <div
      className="inline-flex items-center gap-2"
      data-testid={`agent-sparkline-${slug}`}
      title={`${total} appel(s) sur ${days} jours`}
    >
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="overflow-visible"
        aria-label={`Sparkline ${days}j ${slug}`}
      >
        {series.map((p, i) => {
          const barH = (p.calls / max) * (h - 2);
          return (
            <rect
              key={p.date}
              x={i * barW + 0.5}
              y={h - barH}
              width={Math.max(1, barW - 1)}
              height={barH}
              fill={color}
              opacity={p.calls > 0 ? 0.85 : 0.15}
              rx={1}
            >
              <title>{`${p.date} · ${p.calls} appel(s)`}</title>
            </rect>
          );
        })}
      </svg>
      <span className="text-[10px] text-zinc-500 whitespace-nowrap">
        {total}/30j
      </span>
    </div>
  );
}
